import React from "react";

import { faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { Divider, Grid } from "@material-ui/core";
import { FieldArray, useField } from "formik";
import { isObject, uniq } from "lodash";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";

import { SlimButton } from "components/Button";
import { ButtonIcon } from "components/Button/FontAwesomeButton";
import { Column } from "components/Layout";

import {
  VendorCommissionBandOptions,
  VendorCommissionBandTypes,
} from "constants/vendorCommissionBands";

import { validatePercentage } from "lib/validation";

import { Autocomplete } from "./Autocomplete";
import { Error } from "./Error";
import { Input, PercentageInput } from "./Input";
import { Label } from "./Label";
import { Required } from "./layout";
import { withNamespace } from "./lib";
import {
  SaleSubTypeField,
  SaleTypeField,
  SpeciesField,
} from "components/Form/Fields/SaleFilter";

const getOptionValue = (option: { value: number | null }) => option.value;

export enum ValidationMessage {
  REQUIRED = "Required",
  REQUIRES_SALE_TYPE = "Requires Sale Type to be selected.",
  REQUIRES_SALE_TYPE_SPECIES = "Requires Sale Type and Species to both be selected.",
  NON_UNIQUE = "This combination of Sale Type, Species and Sale Sub Type is not unique.",
}

export const vendorCommissionBandsValidation = Yup.array()
  .nullable()
  .of(
    Yup.object().shape({
      saleType: Yup.string().nullable(true),
      speciesId: Yup.number()
        .nullable(true)
        .test(
          "saleType-required",
          ValidationMessage.REQUIRES_SALE_TYPE,
          function (value: number | null) {
            if (!value) {
              return true;
            }
            const { saleType } = this.parent;
            return !!saleType;
          },
        ),
      saleSubTypeId: Yup.number()
        .nullable(true)
        .test(
          "saleType-species-required",
          ValidationMessage.REQUIRES_SALE_TYPE_SPECIES,
          function (value: number | null) {
            if (!value) {
              return true;
            }
            const { saleType, speciesId } = this.parent;
            return !!saleType && !!speciesId;
          },
        ),
      commissionType: Yup.string().required(ValidationMessage.REQUIRED),
      commissionValue: validatePercentage(12, 6).required(
        ValidationMessage.REQUIRED,
      ),
    }),
  )
  .test(
    "non-dupe",
    ValidationMessage.NON_UNIQUE,
    value =>
      uniq(
        value?.map(
          band => `${band.saleType}|${band.speciesId}|${band.saleSubTypeId}`,
        ),
      ).length === (value?.length || 0),
  );

interface VendorCommissionBandProps {
  index: number;
  name: string;
  namespace?: string;
  removeRow: (index: number) => void;
}

function VendorCommissionBand(props: VendorCommissionBandProps) {
  const { index, name, namespace, removeRow } = props;

  const [{ value: commissionType }] = useField(
    `${withNamespace(namespace, name)}.${index}.commissionType`,
  );

  return (
    <>
      <Grid item xs={6} md={2}>
        <SaleTypeField
          namespace={`${withNamespace(namespace, name)}.${index}`}
        />
      </Grid>
      <Grid item xs={6} md={2}>
        <SpeciesField
          namespace={`${withNamespace(namespace, name)}.${index}`}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <SaleSubTypeField
          namespace={`${withNamespace(namespace, name)}.${index}`}
        />
      </Grid>
      <Grid item xs={6} md={2}>
        <Autocomplete
          name={`${withNamespace(namespace, name)}.${index}.commissionType`}
          options={VendorCommissionBandOptions}
          required
          getOptionValue={getOptionValue}
          disableClearable
        />
      </Grid>
      <Grid item xs={6} md={1}>
        {commissionType === VendorCommissionBandTypes.DollarsPerHead ? (
          <Input
            name={`${withNamespace(namespace, name)}.${index}.commissionValue`}
            beforeSymbol="$"
            multiplier={100}
            type="number"
            decimalPlaces={3}
            decimal
          />
        ) : (
          <PercentageInput
            name={`${withNamespace(namespace, name)}.${index}.commissionValue`}
          />
        )}
      </Grid>
      <Grid item xs={6} md={1} container justifyContent="flex-end">
        <SlimButton
          onClick={() => removeRow(index)}
          backgroundColor="deleteRed"
          type="button"
        >
          <ButtonIcon icon={faTrash} />
          &nbsp;Remove
        </SlimButton>
      </Grid>
      <Grid xs={12} item>
        <Divider />
      </Grid>
    </>
  );
}

const generateDefaultBand = (): Partial<VendorCommissionBand> => ({
  id: uuidv4(),
  businessId: null,
  commissionType: VendorCommissionBandTypes.Percentage,
  commissionValue: 0,
  saleSubTypeId: null,
  saleType: null,
  speciesId: null,
});

interface VendorCommissionBandsProps {
  namespace?: string;
  name?: string;
  label?: string;
  tooltip?: string;
}

export function VendorCommissionBands(props: VendorCommissionBandsProps) {
  const {
    namespace = "",
    name = "vendorCommissionBands",
    label = "Vendor Commissions",
    tooltip,
  } = props;
  const [field, meta, helpers] = useField(withNamespace(namespace, name));

  const { value } = field || { value: [] };
  const { error } = meta || { error: null };

  return (
    <Column fullWidth fullHeight>
      {label ? <Label tooltip={tooltip}>{label}</Label> : null}
      <Column fullWidth fullHeight paddingHorizontal={3}>
        <FieldArray name={withNamespace(namespace, name)}>
          {arrayHelpers => {
            const addNew = () => {
              arrayHelpers.push(generateDefaultBand());
            };
            const removeRow = (index: number) => {
              arrayHelpers.remove(index);
              helpers.setTouched(true);
            };
            return (
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <SlimButton onClick={addNew} type="button">
                    <ButtonIcon icon={faPlus} />
                    &nbsp;New Commission Rate
                  </SlimButton>
                </Grid>
                {!isObject(error) && error ? (
                  <Grid item xs={12}>
                    <Error>{error}</Error>
                  </Grid>
                ) : null}
                {value.length > 0 ? (
                  <>
                    <Grid item xs={6} md={2}>
                      <Label>Sale Type</Label>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Label>Species</Label>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Label>Sale Sub Type</Label>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Label>
                        Commission Type
                        <Required />
                      </Label>
                    </Grid>
                    <Grid item xs={6} md={1}>
                      <Label>
                        Commission Value
                        <Required />
                      </Label>
                    </Grid>
                    <Grid item xs={6} md={1}>
                      &nbsp;
                    </Grid>
                    <Grid xs={12} item>
                      <Divider />
                    </Grid>
                    {value.map((_: VendorCommissionBand, idx: number) => (
                      <VendorCommissionBand
                        index={idx}
                        removeRow={removeRow}
                        name={name}
                        key={idx}
                      />
                    ))}
                  </>
                ) : null}
              </Grid>
            );
          }}
        </FieldArray>
      </Column>
    </Column>
  );
}
